import React from "react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { RadioButtonProps } from "./types";
import {
  radioButtonVariants,
  circleVariants,
  circleLabelClass,
  feedbackMessageClass,
  optionImageClass,
  optionTextClass,
  feedbackWrapperDivClass,
} from "./RadioButton.css";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { tokens } from "@labxchange/design_tokens";
import { useIntl, defineMessages } from "react-intl";
import { X, Check } from "lucide-react";

import { containsMath } from "../mathText";

export const messages = defineMessages({
  correctFeedback: {
    id: "Question.correctFeedback",
    defaultMessage: "Correct!",
    description:
      "Feedback text shown when the user selects the correct answer.",
  },
  incorrectFeedback: {
    id: "Question.incorrectFeedback",
    defaultMessage: "Not quite.",
    description:
      "Feedback text shown when the user selects an incorrect answer.",
  },
  optionLabel: {
    id: "Question.optionLabel",
    defaultMessage: "Option {displayLabel}",
    description:
      "Label for a given option, where {displayLabel} is A, B, C, etc.",
  },
});

export const RadioButton: React.FC<RadioButtonProps> = ({
  option: { equation = false, ...optionData },
  displayLabel,
  isSelected,
  isSubmitted,
  isCorrect,
  showFeedbackOnSubmit = true,
  idPrefix,
}) => {
  const feedbackId = `${idPrefix ? `${idPrefix}-` : ""}${displayLabel}-feedback`;
  const getVariant = () => {
    if (isSubmitted && !isCorrect && showFeedbackOnSubmit) {
      return "incorrect";
    }
    if (isSubmitted && isCorrect && showFeedbackOnSubmit) {
      return "correct";
    }
    if (isSelected || (isSubmitted && !showFeedbackOnSubmit)) {
      return "selected";
    }
    return "default";
  };

  const variant = getVariant();
  const showFeedback = isSubmitted && showFeedbackOnSubmit;
  const intl = useIntl();

  // Generate accessible label for the option
  const getOptionLabel = () => {
    const labelPrefix = intl.formatMessage(messages.optionLabel, {
      displayLabel,
    });

    if (optionData.image && optionData.text) {
      const imageAlt =
        optionData.image.altText && optionData.image.altText.trim()
          ? intl.formatMessage({ id: optionData.image.altText })
          : "";
      const text =
        optionData.text && optionData.text.trim()
          ? intl.formatMessage({ id: optionData.text })
          : "";
      return `${labelPrefix}: ${imageAlt} - ${text}`;
    }

    if (optionData.image) {
      const imageAlt =
        optionData.image.altText && optionData.image.altText.trim()
          ? intl.formatMessage({ id: optionData.image.altText })
          : "";
      return `${labelPrefix}: ${imageAlt}`;
    }

    const text =
      optionData.text && optionData.text.trim()
        ? intl.formatMessage({ id: optionData.text })
        : "";
    return `${labelPrefix}: ${text}`;
  };

  return (
    <>
      <RadioGroup.Item
        value={optionData.id.toString()}
        className={radioButtonVariants[variant]}
        aria-label={getOptionLabel()}
        aria-describedby={showFeedback ? feedbackId : undefined}
        data-variant={variant}
      >
        {showFeedbackOnSubmit ? (
          <div className={circleVariants[variant]}>
            {showFeedback && !isCorrect ? (
              <X color={tokens.questionselector.fail.color_icon} />
            ) : showFeedback && isCorrect ? (
              <Check color={tokens.questionselector.success.color_icon} />
            ) : (
              <div className={circleLabelClass}>
                <p
                  aria-label={intl.formatMessage(messages.optionLabel, {
                    displayLabel,
                  })}
                >
                  {displayLabel}
                </p>
              </div>
            )}
          </div>
        ) : null}

        {/* Render content based on what's available: text, image, or both */}
        {optionData.image && optionData.text ? (
          // Both image and text
          <div
            style={
              optionData.image.fullWidth
                ? { flex: "1 0 0", minWidth: 0 }
                : undefined
            }
          >
            <img
              src={optionData.image.src}
              alt={optionData.image.altText}
              className={optionImageClass}
            />
            <div className={optionTextClass}>
              <RichText value={optionData.text} equation={equation} />
            </div>
          </div>
        ) : optionData.image ? (
          // Image only
          <div
            style={
              optionData.image.fullWidth
                ? { flex: "1 0 0", minWidth: 0 }
                : undefined
            }
          >
            <img
              src={optionData.image.src}
              alt={optionData.image.altText}
              className={optionImageClass}
            />
          </div>
        ) : (
          <span>
            <RichText value={optionData.text} equation={equation} />
          </span>
        )}
      </RadioGroup.Item>

      {/*
        Per-option feedback is a plain, static block associated with its radio
        via aria-describedby (see the RadioGroup.Item above). It is deliberately
        NOT its own aria-live region: on submit the question-level status region
        (in the parent) announces the result once, and this text is read by the
        screen reader as the option's description when the user navigates to it.
      */}
      <div className={feedbackWrapperDivClass[variant]} data-variant={variant}>
        <p className={`${feedbackMessageClass[variant]}`} id={feedbackId}>
          {showFeedback && (
            <>
              <b>
                {isCorrect
                  ? intl.formatMessage(messages.correctFeedback)
                  : intl.formatMessage(messages.incorrectFeedback)}
              </b>{" "}
              <RichText
                value={optionData.feedback}
                equation={containsMath(optionData.feedback)}
              />
            </>
          )}
        </p>
      </div>
    </>
  );
};

export default RadioButton;
